import Link from "next/link";
import {
  AuthorModel,
  EpisodeSummaryModel,
  ImageModel as ImageType,
  RankingSeriesModel,
  SeriesSummaryModel,
  UserModel,
} from "@api/models";
import {
  AnalyticLogProps,
  LOG_CATEGORIES,
  LOG_EVENT_DETAILS,
  makeClickLogDataAttrs,
  makeImpLogDataAttrs,
} from "@lib/analyticLog";
import { ImageWithImpLog } from "./ImageWithImpLog";
import { CategoryName } from "app/(app)/(common)/(common)/category/[categoryName]/CategoryPage";
import { AuthorLink } from "./AuthorLink";

export function SeriesBox({
  series,
  displayIndex,
  displayedOn,
  category,
}: {
  series: SeriesSummaryModel;
  displayIndex: number;
  category?: string;
  displayedOn:
    | "category"
    | "series"
    | "seriesSummary"
    | "seriesRecommend"
    | "search_default"
    | "search_series"
    | "search_author"
    | "genre";
}) {
  const image = series.images?.[0];

  const impLogAttr: AnalyticLogProps = {};
  let clickLogAttr: AnalyticLogProps = impLogAttr;
  if (displayedOn === "category") {
    impLogAttr.logEventDetail = LOG_EVENT_DETAILS.category;
    impLogAttr.logCategory = LOG_CATEGORIES.categoryContinue;
    impLogAttr.logPosition = LOG_CATEGORIES.categoryContinue + (displayIndex + 1);
  } else if (displayedOn === "series") {
    //著者ページ、一覧など
    impLogAttr.logEventDetail = LOG_EVENT_DETAILS.series;
    impLogAttr.logCategory = LOG_CATEGORIES.series;
    impLogAttr.logPosition = String(displayIndex);
    clickLogAttr = Object.assign({}, impLogAttr, { logEventDetail: LOG_EVENT_DETAILS.clickThumb });
  } else if (displayedOn === "seriesSummary") {
    //特集ページなど
    impLogAttr.logEventDetail = LOG_EVENT_DETAILS.seriesSummary;
    impLogAttr.logCategory = LOG_CATEGORIES.series;
    impLogAttr.logPosition = LOG_CATEGORIES.series;
    clickLogAttr = Object.assign({}, impLogAttr, { logEventDetail: LOG_EVENT_DETAILS.clickThumb });
  } else if (displayedOn === "seriesRecommend") {
    //作品ページ下レコメンド枠
    impLogAttr.logEventDetail = LOG_EVENT_DETAILS.category;
    impLogAttr.logCategory = LOG_CATEGORIES.null;
    impLogAttr.logPosition = LOG_CATEGORIES.null + (displayIndex + 1);
    clickLogAttr = Object.assign({}, impLogAttr, { logEventDetail: LOG_EVENT_DETAILS.clickThumb });
  } else if (displayedOn === "search_default") {
    impLogAttr.logEventDetail = LOG_EVENT_DETAILS.search;
    impLogAttr.logCategory = LOG_CATEGORIES.popularSeries;
    impLogAttr.logPosition = LOG_CATEGORIES.popularSeries + displayIndex;
  } else if (displayedOn === "search_series") {
    impLogAttr.logEventDetail = LOG_EVENT_DETAILS.search;
    impLogAttr.logCategory = LOG_CATEGORIES.series;
    impLogAttr.logPosition = String(displayIndex);
  } else if (displayedOn === "search_author") {
    impLogAttr.logEventDetail = LOG_EVENT_DETAILS.seriesTop; //NOTE: ここがseriesTopなのは謎
    impLogAttr.logCategory = LOG_CATEGORIES.list;
    impLogAttr.logPosition = String(displayIndex);
  } else if (displayedOn === "genre") {
    impLogAttr.logEventDetail = LOG_EVENT_DETAILS.genre;
    impLogAttr.logCategory = category;
    impLogAttr.logPosition = impLogAttr.logPosition = String(displayIndex);
  }

  return (
    <SeriesBoxComponent
      indexId={series.indexId}
      image={image}
      updatedOn={series.updatedOn}
      linkUrl={`/series/${series.id}`}
      title={series.name}
      freeArticleCount={series.isOnCampaign ? series.freeArticleCount : undefined}
      authors={series.author}
      isUp={series.isUp}
      impLogAttr={impLogAttr}
      clickLogAttr={clickLogAttr}
    />
  );
}

export function RankingSeriesBox({
  series,
  rank,
  displayIndex,
  category,
}: {
  series: RankingSeriesModel;
  rank: number;
  displayIndex: number;
  category: CategoryName;
}) {
  const image = series.image;

  const logAttrs: AnalyticLogProps = {
    logEventDetail: LOG_EVENT_DETAILS.ranking,
    logCategory: LOG_EVENT_DETAILS.ranking + category,
    logPosition: LOG_EVENT_DETAILS.ranking + category + (displayIndex + 1),
  };
  return (
    <SeriesBoxComponent
      indexId={series.indexId}
      image={image}
      updatedOn={undefined}
      linkUrl={`/series/${series.id}`}
      rank={rank}
      title={series.title}
      authors={series.author}
      isUp={series.isUp}
      clickLogAttr={logAttrs}
      impLogAttr={logAttrs}
    />
  );
}
export function EpisodeBox({
  episode,
  clickLogAttr,
  impLogAttr,
}: {
  episode: EpisodeSummaryModel;
  clickLogAttr: AnalyticLogProps;
  impLogAttr: AnalyticLogProps;
}) {
  const image = episode.thumbnailImages?.[0];

  return (
    <SeriesBoxComponent
      image={image}
      updatedOn={episode.datePublished}
      linkUrl={`/episodes/${episode.id}`}
      title={episode.title}
      clickLogAttr={clickLogAttr}
      impLogAttr={impLogAttr}
      indexId={episode.indexId}
    />
  );
}

function SeriesBoxComponent({
  indexId,
  image,
  updatedOn,
  isUp,
  rank,
  linkUrl,
  title,
  freeArticleCount,
  authors,
  impLogAttr,
  clickLogAttr,
}: {
  indexId?: number;
  image?: ImageType;
  updatedOn?: number;
  rank?: number;
  linkUrl: string;
  title: string;
  freeArticleCount?: number;
  authors?: AuthorModel[] | UserModel[];
  isUp?: boolean;
  impLogAttr: AnalyticLogProps;
  clickLogAttr: AnalyticLogProps;
}) {
  if (!image) {
    image = {
      url: "https://cdn-public.comici.jp/img/loading.svg",
      width: 1200,
      height: 630,
    };
    impLogAttr.logImageId = image.id;
    clickLogAttr.logImageId = image.id;
  }
  impLogAttr.logSeriesId = indexId;
  clickLogAttr.logSeriesId = indexId;
  updatedOn = updatedOn || 0;
  if (isUp === undefined) {
    isUp = new Date().getTime() / 1000 - updatedOn < 7 * 24 * 60 * 60; //7日以内に更新されたかどうか
  }

  return (
    <div className={"series-list-item" + (rank ? ` mode-rank-${rank}` : "")}>
      <Link
        href={linkUrl}
        className="series-list-item-link"
        {...makeClickLogDataAttrs(clickLogAttr)}
      >
        <figure className="series-list-item-fig">
          <ImageWithImpLog
            src={image.url}
            width={image.width || 1200}
            height={image.height || 630}
            alt=""
            className="series-list-item-img"
            {...makeImpLogDataAttrs(impLogAttr)}
          />
        </figure>
        {isUp && (
          <div className="series-list-item-mark-wrap">
            <div className="series-list-item-mark">更新</div>
          </div>
        )}
        {rank && <div className="series-list-item-rank">{rank}</div>}
        <div className="series-list-item-desc">
          <div className="series-list-item-h">
            {/*
            {series?.isCompleted && <span className="">完結</span>}
            {series?.isUp && <span className="">UP</span>}
            {series?.isNew && <span className="">N</span>}
            {series?.enabled && <span>非公開</span>}
            */}
            <span className="">{title}</span>
          </div>
          {/*
          {series?.numberOfFreeArticles && series.numberOfFreeArticles > 0 ? (
            <div className="">{series.numberOfFreeArticles}話無料</div>
          ) : (
            <></>
          )}
        */}
          {freeArticleCount && (
            <div className="series-list-item-campaign">
              キャンペーンにつき{freeArticleCount}話分無料
            </div>
          )}
        </div>
      </Link>
      <div className="series-list-item-author">
        {authors?.map((item, index) => {
          return (
            <AuthorLink
              author={item}
              seriesIndexId={indexId}
              key={item.id}
              displayIndex={index}
              className="series-list-item-author-link"
              showRole={authors.length > 1}
            />
          );
        })}
      </div>
    </div>
  );
}
